import {registerServerSelectMenuWidget} from "@/ui-controls/ServerSelectMenu";
import {requiredElement, suppressFormSubmit} from "@/utils/DOMHelpers";
import {markWorkingPage, unmarkWorkingPage} from "@/ui-components/BusyIndicator";
import {getResponseFailErrorMessage, NetworkError} from "@/network/RequestUtils";
import {Messages} from "@/localization/Messages";
import {isSafeUrl} from "@/utils/UrlValidation";
import {safeToNumber} from "@/utils/ValueUtils";
import {SessionRepository} from "@/network/repository/SessionRepository";
import {updateHistoryUrl} from "@/utils/HistoryUtils";
import {AuthenticationType} from "@/network/model/NetworkUserLoginQueryIDPsResponse";
import $ from "jquery";

enum Stage {
    ENTER_USER_ID,
    SELECT_AUTHENTICATION,
    ENTER_PASSWORD,
    ENTER_PASSWORD_AND_TFA,
}

export class LoginPage {
    private readonly messages: Messages;
    private readonly repository: SessionRepository;
    private stage: Stage = Stage.ENTER_USER_ID;

    // Used when acting on auth selection buttons
    private userId?: string = undefined;
    private samlIdentifier?: string = undefined;

    // Parameters from the URL we will / may use with the login process
    private fromURI: string | undefined;
    private lang: string | undefined;

    private readonly loginFormEl: HTMLFormElement = document.getElementById("loginForm") as HTMLFormElement;

    private readonly stepTitleEl: HTMLHeadingElement = document.getElementById("step-title") as HTMLHeadingElement;

    private readonly userIdWrapperEl: HTMLDivElement = document.getElementById("userIdWrapper") as HTMLDivElement;
    private readonly userIdInputEl: HTMLInputElement = document.getElementById("userId") as HTMLInputElement;
    private readonly userIdLabelEl: HTMLLabelElement = document.getElementById("userIdLabel") as HTMLLabelElement;
    private readonly changeUserIdButtonEl: HTMLButtonElement = document.getElementById("changeUserId") as HTMLButtonElement;
    private readonly changeUserIdSpanEl: HTMLSpanElement = document.getElementById("secondStepUserId") as HTMLSpanElement;

    private readonly authWrapperEl: HTMLDivElement = document.getElementById("authWrapper") as HTMLDivElement;
    private readonly passwordLabelEl: HTMLLabelElement = document.getElementById("passwordLabel") as HTMLLabelElement;
    private readonly passwordInputEl: HTMLInputElement = document.getElementById("password") as HTMLInputElement;
    private readonly passwordVisibilityButtonEl: HTMLButtonElement = document.getElementById("password-visibility-button") as HTMLButtonElement;
    private readonly passwordVisibilityIconEl: HTMLDivElement = document.getElementById("password-visibility-icon") as HTMLDivElement;
    private readonly selectAuthLabelEl: HTMLLabelElement = document.getElementById("selectAuthLabel") as HTMLLabelElement;
    private readonly selectAuthSSOButtonEl: HTMLButtonElement = document.getElementById("selectAuthSSO") as HTMLButtonElement;
    private readonly selectAuthPasswordButtonEl: HTMLButtonElement = document.getElementById("selectAuthPassword") as HTMLButtonElement;
    private readonly forgotPasswordAnchorEl: HTMLAnchorElement = document.getElementById("ForgotMyPassword") as HTMLAnchorElement;
    private readonly forgotPasswordDivEl: HTMLDivElement = document.getElementById("ForgotMyPasswordDiv") as HTMLDivElement;

    private readonly securityCodeWrapperEl: HTMLDivElement = document.getElementById("securityCodeWrapper") as HTMLDivElement;
    private readonly securityCodeInputEl: HTMLInputElement = document.getElementById("securityCode") as HTMLInputElement;

    private readonly errorMessageEl: HTMLElement = requiredElement("error-message");

    private readonly submitWrapperEl: HTMLDivElement = document.getElementById("submitWrapper") as HTMLDivElement;
    private readonly submitButtonEl: HTMLButtonElement = document.getElementById("submit-btn") as HTMLButtonElement;

    private $serverSelect: JQuery<HTMLElement>;

    constructor(messages: Messages, repository: SessionRepository) {
        this.messages = messages;
        this.repository = repository;
    }

    private updateFieldStates(): void {
        this.stepTitleEl.textContent = this.stage === Stage.ENTER_USER_ID
            ? this.messages.prop("Login_EnterUserId_Title")
            : this.messages.prop("Login_EnterPassword_Title");

        this.userIdWrapperEl.classList.toggle("hidden", false);
        // User ID label
        this.userIdLabelEl.classList.toggle("hidden", this.stage !== Stage.ENTER_USER_ID);
        // User ID input
        this.userIdInputEl.classList.toggle("hidden", this.stage !== Stage.ENTER_USER_ID);
        this.userIdInputEl.required = this.stage === Stage.ENTER_USER_ID;
        this.userIdInputEl.readOnly = this.stage === Stage.ENTER_PASSWORD;
        // Change user ID anchor
        this.changeUserIdButtonEl.classList.toggle("hidden", this.stage === Stage.ENTER_USER_ID);

        this.authWrapperEl.classList.toggle("hidden", this.stage === Stage.ENTER_USER_ID);
        // Password Label
        this.passwordLabelEl.classList.toggle("hidden", this.stage === Stage.ENTER_USER_ID || this.stage === Stage.SELECT_AUTHENTICATION);
        // Password input
        this.passwordInputEl.classList.toggle("hidden", this.stage === Stage.ENTER_USER_ID || this.stage === Stage.SELECT_AUTHENTICATION);
        this.passwordInputEl.required = this.stage === Stage.ENTER_PASSWORD || this.stage === Stage.ENTER_PASSWORD_AND_TFA;
        this.passwordVisibilityButtonEl.classList.toggle("hidden", this.stage === Stage.ENTER_USER_ID || this.stage === Stage.SELECT_AUTHENTICATION);
        // Select Auth Label
        this.selectAuthLabelEl.classList.toggle("hidden", this.stage !== Stage.SELECT_AUTHENTICATION);
        // Select Auth buttons
        if (this.stage === Stage.SELECT_AUTHENTICATION) {
            this.selectAuthSSOButtonEl.classList.toggle("hidden", !this.samlIdentifier);
            this.selectAuthPasswordButtonEl.classList.toggle("hidden", !this.userId);
        } else {
            this.selectAuthSSOButtonEl.classList.add("hidden");
            this.selectAuthPasswordButtonEl.classList.add("hidden");
        }
        // Forgot password section
        this.forgotPasswordDivEl.classList.toggle("hidden", this.stage === Stage.ENTER_USER_ID);

        this.securityCodeWrapperEl.classList.toggle("hidden", this.stage !== Stage.ENTER_PASSWORD_AND_TFA);
        this.securityCodeInputEl.required = this.stage === Stage.ENTER_PASSWORD_AND_TFA;

        this.submitWrapperEl.classList.toggle("hidden", this.stage === Stage.SELECT_AUTHENTICATION);
        // Submit button text (we use text from data attributes as we don't have
        // localized messages available pre-login -- although maybe we should).
        this.submitButtonEl.textContent =
            this.stage === Stage.ENTER_PASSWORD || this.stage === Stage.ENTER_PASSWORD_AND_TFA
                ? this.messages.prop("Button_Login")
                : this.messages.prop("Button_Next");
    }

    private showErrorMessage(message: string): void {
        this.errorMessageEl.classList.remove("hidden");
        this.errorMessageEl.textContent = message;
    }

    private clearErrorMessage(): void {
        this.errorMessageEl.classList.add("hidden");
        this.errorMessageEl.textContent = "";
    }

    private async onChangeUserIdClicked(e: PointerEvent): Promise<void> {
        // FUTURE: Remove the event suppression when migrated to a button instead of an Anchor
        e.preventDefault();
        e.stopPropagation();
        this.transitionToEnterUserIdStage();
    }

    private transitionToEnterUserIdStage() {
        this.stage = Stage.ENTER_USER_ID;
        this.changeUserIdSpanEl.textContent = "";
        this.passwordInputEl.value = "";
        this.securityCodeInputEl.value = "";
        this.updateFieldStates();
        this.clearErrorMessage();
        this.userIdInputEl.focus();
    }

    private async onSubmitClicked(): Promise<void> {
        // Validate the form and don't proceed if there are issues
        if (!this.loginFormEl.reportValidity()) {
            return;
        }
        this.clearErrorMessage();

        switch (this.stage) {
            case Stage.ENTER_USER_ID:
                await this.onEnterUserIdSubmit();
                break;

            case Stage.ENTER_PASSWORD:
            case Stage.ENTER_PASSWORD_AND_TFA:
                await this.onEnterPasswordSubmit();
                break;
        }
    }

    private navigateToSaml(samlIdentifier?: string): void {
        if (!samlIdentifier) {
            return;
        }
        // Redirect to the SAML endpoint
        location.href = "saml/go/" + encodeURIComponent(samlIdentifier) + location.search;
    }

    private populateUserIdChangeControls(userId: string) {
        // Populate the ARIA label and tooltip with the user id in the
        // "change user id" element
        const text = this.messages.prop("Login_change_userid_aria_label", userId);
        this.changeUserIdButtonEl.ariaLabel = text;
        this.changeUserIdButtonEl.title = text;

        this.changeUserIdSpanEl.textContent = userId;
    }

    private transitionToEnterPasswordStage(userId: string): void {
        this.populateUserIdChangeControls(userId);
        this.stage = Stage.ENTER_PASSWORD;
        this.updateFieldStates();
        this.passwordInputEl.focus();
    }

    private transitionToChooseAuthenticationMethodStage(
        userId: string,
        entries: string[],
        samlIdentifier: string
    ) {
        this.populateUserIdChangeControls(userId);
        this.userId = entries.includes(AuthenticationType.BUILTIN) ? userId : undefined;
        this.samlIdentifier = entries.includes(AuthenticationType.SAML) ? samlIdentifier : undefined;
        this.stage = Stage.SELECT_AUTHENTICATION;
        this.updateFieldStates();
        // FUTURE: Actually pick the first visible one (although for now this
        //  one will always be visible)
        this.selectAuthSSOButtonEl.focus();
    }

    private async onEnterUserIdSubmit(): Promise<void> {
        // Query the IDPs from the server to see what options we
        // have for the user for logging in (and move them to the next
        // step, or redirect to IDP)
        const userId = this.userIdInputEl.value.trim();

        const response = await this.repository.queryIDPs(userId);
        if (response.entries.length === 1 && response.entries.at(0) === AuthenticationType.SAML && response.samlIdentifier) {
            this.navigateToSaml(response.samlIdentifier);
        } else if (response.entries.length > 1 && response.samlIdentifier) {
            this.transitionToChooseAuthenticationMethodStage(
                userId,
                response.entries,
                response.samlIdentifier
            );
        } else {
            this.transitionToEnterPasswordStage(userId);
        }
    }

    private togglePasswordVisibility() {
        this.passwordInputEl.type = this.passwordInputEl.type === "password" ? "text" : "password";
        this.passwordVisibilityIconEl.innerText =
            this.passwordInputEl.type === "password" ? "visibility_off" : "visibility"; // NOSONAR(typescript:S2068) False positive; thinks this is a password
    }

    private async onEnterPasswordSubmit(): Promise<void> {
        markWorkingPage();
        try {
            // Hide any visible password so it doesn't linger on the screen any
            // longer than necessary
            this.passwordInputEl.type = "password"; // NOSONAR(typescript:S2068) - false positive - not a hard-coded password
            this.passwordVisibilityIconEl.innerText = "visibility_off"; // NOSONAR(typescript:S2068) - false positive - not a hard-coded password

            const result = await this.repository.performLogin(
                this.userIdInputEl.value,
                this.passwordInputEl.value,
                this.stage === Stage.ENTER_PASSWORD_AND_TFA
                    ? safeToNumber(this.securityCodeInputEl.value)
                    : undefined,
                this.fromURI,
                this.lang,
            );
            // Success -- save any data (which is nothing for now as we aren't
            // a "one-page" app at the moment) and redirect the user...
            console.log("Logged in: userId=%s, personId=%d",
                result.userId, result.personId);
            const url = result.redirectUrl || this.fromURI;
            if (url && isSafeUrl(url)) {
                console.log("Navigating to %s", url);
                location.href = url;
            } else {
                console.error(
                    "Redirection URL may not be safe, ignoring. url=%s",
                    url
                );
                location.href = "sc/Dashboard";
            }
        } catch (error) {
            if (error instanceof Error && this.repository.isSecurityCodeRequired(error)) {
                console.log("TFA security code required");
                this.transitionToEnterPasswordAndTfaCodeStage();
            } else {
                this.showErrorMessage(
                    getResponseFailErrorMessage(
                        error instanceof NetworkError ? error.statusCode : undefined,
                        error instanceof NetworkError ? error.responseText : undefined,
                    )
                );
                // Reset password and TFA fields on authentication errors
                this.passwordInputEl.value = "";
                this.securityCodeInputEl.value = "";
                this.passwordInputEl.focus();
            }
        } finally {
            unmarkWorkingPage();
        }
    }

    private transitionToEnterPasswordAndTfaCodeStage() {
        // Clear TFA, but not the password as we will re-use it
        this.securityCodeInputEl.value = "";
        this.stage = Stage.ENTER_PASSWORD_AND_TFA;
        this.updateFieldStates(); // Update before setting input focus
        this.securityCodeInputEl.focus();
    }

    private onForgotPasswordClicked(): void {
        // Save the user-supplied userId in sessionStorage before going to the
        // forgot password page so it can pre-fill the field for the user
        globalThis.sessionStorage.setItem("suppliedUserId", this.userIdInputEl.value);
    }

    private get canProceed(): boolean {
        const userIdEntered = this.userIdInputEl.value.trim() !== "";
        const passwordEntered = this.passwordInputEl.value.trim() !== "";
        const securityCodeEntered = this.securityCodeInputEl.value.length === 6;
        switch (this.stage) {
            case Stage.ENTER_USER_ID:
                return userIdEntered;
            case Stage.ENTER_PASSWORD:
                return userIdEntered && passwordEntered;
            case Stage.ENTER_PASSWORD_AND_TFA:
                return userIdEntered && passwordEntered && securityCodeEntered;
            default:
                return false;
        }
    }

    private onEnterAttemptToProceed(e: KeyboardEvent): void {
        if (e.key === "Enter" && this.canProceed) {
            e.preventDefault();
            this.submitButtonEl.click();
        }
    }

    private removeNonNumericInput(e: Event): void {
        if (e.target instanceof HTMLInputElement) {
            e.target.value = e.target.value.replaceAll(/\D/g, "");
        }
    }

    // TS6385 JQueryEventObject is deprecated, but the JQueryUI type definitions have not been updated
    // noinspection JSDeprecatedSymbols
    private onServerSelect(
        event: JQueryEventObject, // NOSONAR(typescript:S1874)
        _ui: JQueryUI.SelectMenuUIParams,
    ) {
        const url = $(event.target).val()?.toString();
        if (url) {
            event.stopPropagation();
            event.preventDefault();
            // Reselect the default server, so if they navigate
            // back, it will be on the right entry.
            this.$serverSelect
                .val("")
                .serverSelectMenu("refresh");
            // Navigate to the selected server URL
            globalThis.location.href = url;
        }
    }

    private initServerSelect() {
        const serverSelectElement = document.getElementById("server-select");
        if (serverSelectElement) {
            // Ideally, this would be done globally, but we don't currently use it
            // on "production", so we will defer registering it unless it's required.
            registerServerSelectMenuWidget();

            // Bind the jQuery select UI to the select element
            this.$serverSelect = $('#server-select').serverSelectMenu({
                width: undefined,
                select: this.onServerSelect.bind(this),
            });
            this.$serverSelect.serverSelectMenu('menuWidget');
        }
    }

    async init(): Promise<void> {
        const loginForm = document.getElementById("loginForm");
        if (!loginForm) {
            // Unsupported browser, so do nothing (as there is nothing on the page)
            return;
        }

        const urlParams = new URLSearchParams(location.search);
        this.fromURI = urlParams.get("fromURI") || undefined;
        this.lang = urlParams.get("lang") || undefined;
        const message = urlParams.get("message");

        // Suppress default form submissions for all forms (serverForm and loginForm)
        suppressFormSubmit(document.getElementById("LoginPage"));

        // Note that we do not update the "submit" button state based on the
        // field contents to avoid weird issues with password fillers and the
        // button not enabling.

        this.userIdInputEl
            .addEventListener("keyup", this.onEnterAttemptToProceed.bind(this));
        this.passwordInputEl
            .addEventListener("keyup", this.onEnterAttemptToProceed.bind(this));
        this.passwordVisibilityButtonEl
            .addEventListener("click", this.togglePasswordVisibility.bind(this));
        this.securityCodeInputEl
            .addEventListener("keyup", this.onEnterAttemptToProceed.bind(this));
        this.securityCodeInputEl
            .addEventListener("input", this.removeNonNumericInput.bind(this));
        this.submitButtonEl
            .addEventListener("click", this.onSubmitClicked.bind(this));
        this.changeUserIdButtonEl
            .addEventListener("click", this.onChangeUserIdClicked.bind(this));
        this.selectAuthSSOButtonEl
            .addEventListener("click", () => this.navigateToSaml(this.samlIdentifier));
        this.selectAuthPasswordButtonEl
            .addEventListener("click", () => this.transitionToEnterPasswordStage(this.userIdInputEl.value));
        this.forgotPasswordAnchorEl
            .addEventListener("click", this.onForgotPasswordClicked.bind(this));

        this.initServerSelect();
        this.updateFieldStates();

        // Remove any persisted user id for the forgot password page
        sessionStorage.removeItem("suppliedUserId");

        // See if we have an error message in the URL parameters and show it if
        // we do (and clear from the URL as well)
        if (message) {
            this.showErrorMessage(message);
            // Remove the message from the URL parameters and update the
            //  current URL
            const newParams = new URLSearchParams(urlParams);
            newParams.delete("message");
            updateHistoryUrl(newParams, undefined, "placeholder-history-data");
        } else {
            this.clearErrorMessage();
        }

        // Set the initial focus on first load
        this.userIdInputEl.focus();
    }

}
